import { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSite } from '../config/siteConfig';
import { ENV_CONFIG } from '../config/environment';
import loginDrawing from '../assets/login-mine-drawing.png';
import './Login.css';

function BrandMark({ logoSrc, productName }) {
  if (logoSrc) {
    return <img src={logoSrc} alt="" className="login-logo-img" />;
  }
  const initials = String(productName || 'MD')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span className="login-mark" aria-hidden>
      <svg viewBox="0 0 48 48" className="login-mark-svg">
        <path d="M24 4L44 16v16L24 44 4 32V16L24 4z" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M24 4v40M4 16l20 12 20-12" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.7" />
      </svg>
      <span className="login-mark-letters">{initials}</span>
    </span>
  );
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const site = getSite();
  const productName = site.productName || ENV_CONFIG.PRODUCT_NAME;
  const projectName = site.projectName || ENV_CONFIG.PROJECT_NAME;

  const logoSrc = useMemo(() => {
    const url = site.logoUrl;
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    return `${base}/${String(url).replace(/^\//, '')}`;
  }, [site.logoUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Enter your email and password to continue.');
      setLoading(false);
      return;
    }

    const result = await login(email, password);
    if (!result.success) {
      setError(result.error || 'Sign-in failed. Check your details and try again.');
    }
    setLoading(false);
  };

  return (
    <div className="login-root">
      <div className="login-bg" aria-hidden>
        <img src={loginDrawing} alt="" className="login-drawing" />
        <div className="login-vignette" />
      </div>

      <div className="login-shell">
        <form onSubmit={handleSubmit} className="login-card" noValidate>
          <header className="login-brand">
            <BrandMark logoSrc={logoSrc} productName={productName} />
            <div>
              <h1 className="login-product">{productName}</h1>
              <p className="login-project">{projectName}</p>
            </div>
          </header>

          <label className="login-field">
            <span>Email</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="username"
              enterKeyHint="next"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
              autoFocus
            />
          </label>

          <label className="login-field">
            <span>Password</span>
            <span className="login-password">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                enterKeyHint="go"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
              <button
                type="button"
                className="login-eye"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </span>
          </label>

          {error ? (
            <p className="login-error" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Enter viewer'}
          </button>

          <p className="login-foot">Authorized personnel · Session stays on this device</p>
        </form>
      </div>
    </div>
  );
};

export default Login;
